import React from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'antd';
import { Input, RichArea, Select } from '_atoms';
import { SELECT_TYPES } from '_atoms/Select/constants';
import { TRANSLATION_LANGUAGE_OPTIONS } from 'constants/promotionRequest';
import type { SelectOption } from '_atoms/Select/types';

interface PromotionRequestMainInfoProps {
  requiredRule: { required: boolean; message: string }[];
  technologyAreas: SelectOption[];
  searchRegionOptions: SelectOption[];
  recruiterLeadOptions: SelectOption[];
  teamLeadList: SelectOption[];
  lockUntilOpening?: boolean;
  loading?: boolean;
}

export const PromotionRequestMainInfo: React.FC<PromotionRequestMainInfoProps> = ({
  requiredRule,
  technologyAreas,
  searchRegionOptions,
  recruiterLeadOptions,
  teamLeadList,
  lockUntilOpening = true,
  loading,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Form.Item
        noStyle
        shouldUpdate={(prev, curr) =>
          prev.openings !== curr.openings || prev.description_initial !== curr.description_initial
        }
      >
        {({ getFieldValue, setFieldValue }) => {
          const hasOpening = (getFieldValue('openings') || []).some(row => row?.opening?.value);
          const disabled = lockUntilOpening && !hasOpening;
          return (
            <>
              <Row gutter={[32, 16]}>
                <Col lg={12} md={12} sm={24}>
                  <Input
                    label={t('translation:create_promotion_request.label_request_title')}
                    placeholder={t('translation:create_promotion_request.ph_request_title')}
                    maxLength={500}
                    disabled={disabled}
                    itemProps={{ name: 'request_title', rules: requiredRule }}
                  />
                </Col>
              </Row>
              <Form.Item className="rich-area-item" name="description" rules={requiredRule}>
                <RichArea
                  initialValue={getFieldValue('description_initial')}
                  label={t('translation:create_promotion_request.label_description')}
                  placeholder={t('translation:create_promotion_request.ph_description')}
                  onChange={value => setFieldValue('description', value)}
                  disabled={disabled}
                  maxLength={20_000}
                />
              </Form.Item>
              <Row gutter={[32, 16]}>
                <Col lg={12} md={12} sm={24}>
                  <Select
                    labelInValue
                    allowClear
                    mode={SELECT_TYPES.MULTIPLE}
                    label={t('translation:create_promotion_request.label_search_region')}
                    placeholder={t('translation:create_promotion_request.ph_search_region')}
                    options={searchRegionOptions}
                    loading={loading}
                    disabled={disabled}
                    itemProps={{ name: 'search_regions', rules: requiredRule }}
                  />
                </Col>
              </Row>
              <Row gutter={[32, 16]}>
                <Col lg={12} md={12} sm={24}>
                  <Select
                    labelInValue
                    allowClear
                    mode={SELECT_TYPES.MULTIPLE}
                    label={t('translation:create_promotion_request.label_technology_area')}
                    placeholder={t('translation:create_promotion_request.ph_technology_area')}
                    options={technologyAreas}
                    loading={loading}
                    itemProps={{ name: 'technology_areas', rules: requiredRule }}
                  />
                </Col>
              </Row>
            </>
          );
        }}
      </Form.Item>

      <Row gutter={[32, 16]}>
        <Col lg={12} md={12} sm={24}>
          <Select
            labelInValue
            allowClear
            mode={SELECT_TYPES.MULTIPLE}
            label={t('translation:create_promotion_request.label_translation_languages')}
            placeholder={t('translation:create_promotion_request.ph_translation_languages')}
            options={TRANSLATION_LANGUAGE_OPTIONS}
            optional
            itemProps={{ name: 'translation_languages' }}
          />
        </Col>
      </Row>

      <Form.Item
        noStyle
        shouldUpdate={(prev, curr) => prev.additional_requirements_initial !== curr.additional_requirements_initial}
      >
        {({ getFieldValue, setFieldValue }) => (
          <Form.Item className="rich-area-item" name="additional_requirements">
            <RichArea
              initialValue={getFieldValue('additional_requirements_initial')}
              label={t('translation:create_promotion_request.label_additional_requirements')}
              placeholder={t('translation:create_promotion_request.ph_additional_requirements')}
              onChange={value => setFieldValue('additional_requirements', value)}
              maxLength={10_000}
              optional
            />
          </Form.Item>
        )}
      </Form.Item>

      <Row gutter={[32, 16]}>
        <Col lg={12} md={12} sm={24}>
          <Select
            labelInValue
            allowClear
            mode={SELECT_TYPES.MULTIPLE}
            label={t('translation:create_promotion_request.label_recruiter_lead')}
            placeholder={t('translation:create_promotion_request.ph_recruiter_lead')}
            options={recruiterLeadOptions}
            loading={loading}
            itemProps={{ name: 'recruiter_leads', rules: requiredRule }}
          />
        </Col>
      </Row>
      <Row gutter={[32, 16]}>
        <Col lg={12} md={12} sm={24}>
          <Select
            labelInValue
            allowClear
            mode={SELECT_TYPES.MULTIPLE}
            label={t('translation:create_promotion_request.label_notification_recipients')}
            placeholder={t('translation:create_promotion_request.ph_notification_recipients')}
            options={teamLeadList}
            loading={loading}
            optional
            itemProps={{ name: 'team_lead_notification_recipients' }}
          />
        </Col>
      </Row>
    </>
  );
};
